import React from 'react';
import { Award, Users, Target, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';

const About = () => {
  const { stats } = mockData;

  const values = [
    {
      icon: Award,
      title: 'Calidad y Atención al Detalle',
      description: 'Trabajo de la más alta calidad posible, enfocándonos en calidad sobre cantidad'
    },
    {
      icon: Users,
      title: 'Integridad y Honestidad',
      description: 'Mantenemos los más altos niveles de profesionalismo en todas nuestras relaciones'
    },
    {
      icon: Target,
      title: 'Compromiso con el Cliente',
      description: 'Priorizamos a nuestros clientes en todo momento y superamos expectativas'
    },
    {
      icon: Heart,
      title: 'Trabajo en Equipo',
      description: 'Cultura centrada en las personas construida sobre una base de confianza'
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 mb-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB0ZWFtfGVufDB8fHx8MTc2MzA0NzQ1MHww&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Sobre Copal Enterprises</h1>
          <p className="text-xl max-w-3xl mx-auto font-light">
            Empresa familiar en Dallas, Texas - Renovation made easy!
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Company Story */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Nuestra Historia</h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed font-light">
                <p>
                  Copal Enterprises LLC es una empresa familiar ubicada en Dallas, Texas, especializada
                  en remodelación general y reparaciones. Nacimos con una visión clara: hacer que la
                  renovación sea fácil para nuestros clientes.
                </p>
                <p>
                  Como contratistas generales, nos enfocamos en proporcionar el trabajo de la más alta
                  calidad posible, priorizando la calidad sobre la cantidad y prestando atención extra
                  a cada detalle. Cada proyecto es tratado con el máximo profesionalismo, integridad y
                  honestidad.
                </p>
                <p>
                  Nuestro equipo de profesionales altamente capacitados se dedica a garantizar la longevidad
                  de nuestra empresa manteniendo los más altos niveles de profesionalismo en todas nuestras
                  relaciones. Con más de 200 proyectos completados, seguimos comprometidos con la excelencia.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB0ZWFtfGVufDB8fHx8MTc2MzA0NzQ1MHww&ixlib=rb-4.1.0&q=85"
                alt="Team"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1694521787193-9293daeddbaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc2Mjk4MTM0N3ww&ixlib=rb-4.1.0&q=85"
                alt="Construction"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1558227691-41ea78d1f631?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc2Mjk4MTM0N3ww&ixlib=rb-4.1.0&q=85"
                alt="Workers"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg"
                alt="Project"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-gradient-to-br from-[#0057CB] to-[#38B6FF] rounded-2xl mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 px-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-100 text-sm md:text-base font-light">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Nuestros Valores
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#7BCDFF]/20 rounded-full mb-4">
                      <Icon className="w-8 h-8 text-[#0057CB]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 font-light">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="grid md:grid-cols-2 gap-8">
          <Card className="bg-gradient-to-br from-[#7BCDFF]/10 to-white border-0">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Misión</h3>
              <p className="text-gray-700 text-lg leading-relaxed font-light">
                Proporcionar el trabajo de la más alta calidad posible, enfocándonos en la calidad
                sobre la cantidad y prestando atención extra a cada detalle. Priorizamos a nuestros
                clientes en todo momento y garantizamos la longevidad de nuestra empresa manteniendo
                los más altos niveles de profesionalismo, integridad, honestidad y justicia en todas
                nuestras relaciones.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#38B6FF]/10 to-white border-0">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Visión</h3>
              <p className="text-gray-700 text-lg leading-relaxed font-light">
                Convertirnos en la opción preferida de nuestros clientes alcanzando la excelencia
                en calidad y proyectos de valor agregado completados a tiempo. Tener una cultura
                diversa y centrada en las personas, construida sobre una base de confianza, donde
                cada proyecto refleje nuestro compromiso con la excelencia.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default About;
